/**
 * MenuBar.jsx — Top chrome bar for the Loom workspace.
 *
 * This is now a slim orchestrator (~220 lines). All individual menus
 * live in ./menus/  (FileMenu, SetupMenu, EditMenu, ViewMenu, ModeMenu,
 * GenreMenu, ToolsMenu, AdvancedModelMenu, HelpMenu).
 *
 * Responsibilities kept here:
 *   • Dialog state (exit, bulk wizard, synopsis hub, model roles, flow pass)
 *   • loom:unsaved-changes / loom:save-and-exit event wiring
 *   • model-roles-open / model-roles-changed event wiring
 *   • bulk-novel-wizard-open event wiring
 *   • Save & Sign Out button
 *   • Right-side toolbar (Simple Mode, cost pills, exports, Ask AI, Rebuild)
 *   • Dialog renders (BulkNovelWizard, StudioProductionDashboard,
 *     TrainingCenter, SynopsisToolsHub, ModelRolesDialog, FlowPassPanel)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import {
  Sparkles, Play, Hammer, Feather, SlidersHorizontal, Lock,
} from 'lucide-react';
import { toast } from 'sonner';
import { usePW } from '@/lib/store';
import { projectsApi } from '@/lib/api';
import { useAuth } from '@/lib/auth_context';
import { useCapabilities } from '@/lib/useCapabilities';
import { providerHandle } from '@/lib/providerLabel';
import { playSaveReminderChime } from '@/lib/saveReminderSound';

import ExportsIndicator from './ExportsIndicator';
import RunningCostPill from './RunningCostPill';
import ManuscriptCostPill from './ManuscriptCostPill';
import WordCreditBalancePill from './WordCreditBalancePill';
import LanguageCapsule from '@/components/LanguageCapsule';

import BulkNovelWizard from '@/components/dialogs/BulkNovelWizard';
import NovelFactoryWizard from '@/components/dialogs/NovelFactoryWizard';
import SynopsisToolsHub from '@/components/dialogs/SynopsisToolsHub';
import ModelRolesDialog from '@/components/dialogs/ModelRolesDialog';
import AiContentAuditDialog from '@/components/dialogs/AiContentAuditDialog';
import TrainingCenter from '@/components/TrainingCenter';
import StudioProductionDashboard from '@/components/StudioProductionDashboard';
import { FlowPassPanel } from '@/components/panels/FlowPassPanel';
import RebuildWizardDialog from '@/components/dialogs/RebuildWizardDialog';
import MetadataExtractionDialog from '@/components/dialogs/MetadataExtractionDialog';

import { FileMenu } from './menus/FileMenu';
import { SetupMenu } from './menus/SetupMenu';
import { EditMenu } from './menus/EditMenu';
import { ViewMenu } from './menus/ViewMenu';
import { ModeMenu } from './menus/ModeMenu';
import { GenreMenu } from './menus/GenreMenu';
import { ToolsMenu } from './menus/ToolsMenu';
import { AdvancedModelMenu } from './menus/AdvancedModelMenu';
import { HelpMenu } from './menus/HelpMenu';

// Model ID → display name (used for the "model on manuscript" toast).
const MODEL_LABELS = {
  'claude-opus-4-7':   'Claude Opus 4.7',
  'claude-sonnet-4-5': 'Claude Sonnet 4.5',
  'gpt-5-2':           'GPT-5.2',
  'gpt-5-4':           'GPT-5.4',
  'gemini-3-flash':    'Gemini 3 Flash',
  'gemini-3-1-pro':    'Gemini 3.1 Pro',
  'deepseek-flash':    'DeepSeek V4.1 Flash',
};

export function MenuBar({
  onNewProject, onNewFromTemplate, onSettings, onOpenSettings, onAbout, onBuyTokens, onExport,
  onSaveAs,
  onImportManuscript, onImportSynopsis, onImportThreeActSynopsis,
  onGenerateSynopsisFromLogline, onBuildSynopsis, onPublishToBookstore,
  onMyRoyalties, onIsbnManager, onCanvaCover, onAccountConnections, onAudiobook, onNarrationStudio,
  onOpenStacks, onOpenFunds, onHelp, onContextHelp, onCommunity,
  onBookCoaching, onRequestEditorReview, onScriptConverter, onKdpMetadata,
  visible, togglePanel, simpleMode, onToggleSimpleMode,
  // Onboarding glow props — threaded to FileMenu
  glowFileTrigger, glowLoadManuscript, onFileMenuOpen, onLoadManuscriptClicked,
}) {
  const { t } = useTranslation();
  const { project, updateProject, setProject, rebuildNovel, building, buildProgress, buildJob, chapters, activeChapterId } = usePW();
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { can: capsCan, upgradeCta } = useCapabilities();

  // ── Dialog states ─────────────────────────────────────────────────
  const [exitOpen,        setExitOpen]        = useState(false);
  const [flowPassOpen,    setFlowPassOpen]     = useState(false);
  const [bulkOpen,        setBulkOpen]         = useState(false);
  const [bulkPreloadFwId, setBulkPreloadFwId]  = useState('');
  const [novelFactoryOpen, setNovelFactoryOpen] = useState(false);
  const [synopsisHubOpen, setSynopsisHubOpen]  = useState(false);
  const [metadataExtractOpen, setMetadataExtractOpen] = useState(false);
  const [modelRolesOpen,  setModelRolesOpen]   = useState(false);
  const [wizardOpen,      setWizardOpen]       = useState(false);
  const [aiAuditOpen,     setAiAuditOpen]      = useState(false);

  // ── Unsaved-changes audio chime (fires playSaveReminderChime on first dirty) ──
  const wasDirtyRef = useRef(false);
  useEffect(() => {
    const onChange = (e) => {
      const dirty = !!e?.detail?.dirty;
      if (dirty && !wasDirtyRef.current) playSaveReminderChime();
      wasDirtyRef.current = dirty;
    };
    window.addEventListener('loom:unsaved-changes', onChange);
    return () => window.removeEventListener('loom:unsaved-changes', onChange);
  }, []);

  // loom:open-flow-pass — dispatched by right-click menu, WorkflowRibbon, WritingCoachCard
  useEffect(() => {
    const open = () => setFlowPassOpen(true);
    window.addEventListener('loom:open-flow-pass', open);
    return () => window.removeEventListener('loom:open-flow-pass', open);
  }, []);

  // ── Model-roles dialog event wiring ───────────────────────────────
  useEffect(() => {
    const open = () => setModelRolesOpen(true);
    const refreshProject = () => {
      if (!project?.id) return;
      // Use setProject (not updateProject) — the model roles were already saved to DB
      // by setModelRoles. updateProject would fire a redundant PUT that can fail the
      // tier-policy check for non-God-mode accounts, silently leaving the store stale.
      projectsApi.get(project.id).then((p) => setProject(p)).catch(() => {});
    };
    window.addEventListener('model-roles-open',    open);
    window.addEventListener('model-roles-changed', refreshProject);
    return () => {
      window.removeEventListener('model-roles-open',    open);
      window.removeEventListener('model-roles-changed', refreshProject);
    };
  }, [project?.id, setProject]);

  // One-per-novel toast telling writers which model is on the manuscript.
  useEffect(() => {
    if (!project?.id) return;
    const key = `pw:model-roles-notice:${project.id}`;
    if (localStorage.getItem(key)) return;
    let cancelled = false, tries = 0;
    const label = (id) => (MODEL_LABELS[id] || id);
    const tick = async () => {
      if (cancelled) return;
      tries += 1;
      const blocked = document.querySelector('[role="dialog"], [data-tour-popover]');
      if (blocked && tries < 40) { setTimeout(tick, 2000); return; }
      try {
        const r = await projectsApi.modelRoles(project.id);
        if (cancelled) return;
        localStorage.setItem(key, '1');
        toast.info(`${label(r.manuscript_model_id)} is writing this manuscript`, {
          description: `${label(r.chat_model_id)} answers in the Chat panel. Change either any time.`,
          duration: 12000,
          action: { label: 'Change', onClick: () => setModelRolesOpen(true) },
        });
      } catch { /* non-critical */ }
    };
    const timer = setTimeout(tick, 2500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [project?.id]);

  // ── Bulk Novel Wizard event wiring ────────────────────────────────
  useEffect(() => {
    const onOpen = (e) => {
      setBulkPreloadFwId(e?.detail?.framework_id || '');
      setBulkOpen(true);
    };
    window.addEventListener('bulk-novel-wizard-open', onOpen);
    return () => window.removeEventListener('bulk-novel-wizard-open', onOpen);
  }, []);

  // ── Provider-aware "Ask AI" button ───────────────────────────────
  const claudeChatCap = capsCan('claude_chat');
  const providerFromModelId = (id) => {
    if (!id) return null;
    if (id.startsWith('gpt-'))    return { provider: 'openai' };
    if (id.startsWith('gemini-')) return { provider: 'gemini' };
    if (id.startsWith('claude-')) return { provider: 'anthropic' };
    return null;
  };
  const handle = providerHandle(providerFromModelId(project?.model_id)) || 'AI';

  return (
    <div className="chrome-bg flex items-center justify-between border-b border-chrome-border px-1.5">
      {/* ── Left side: menus ── */}
      <div className="flex items-center gap-1.5">
        <div className="flex items-center gap-0.5">
          <FileMenu
            onNewProject={onNewProject} onSaveAs={onSaveAs}
            onPublishToBookstore={onPublishToBookstore} onMyRoyalties={onMyRoyalties}
            onIsbnManager={onIsbnManager} onAudiobook={onAudiobook} onNarrationStudio={onNarrationStudio}
            onBookCoaching={onBookCoaching} onRequestEditorReview={onRequestEditorReview}
            onScriptConverter={onScriptConverter} onKdpMetadata={onKdpMetadata}
            onCanvaCover={onCanvaCover} onAccountConnections={onAccountConnections}
            onOpenStacks={onOpenStacks} onOpenFunds={onOpenFunds}
            onExport={onExport} onSettings={onSettings}
            simpleMode={simpleMode}
            onOpenBulk={() => setBulkOpen(true)}
            onOpenNovelFactory={() => setNovelFactoryOpen(true)}
            onExitRequest={() => setExitOpen(true)}
            onImportManuscript={onImportManuscript}
            glowTrigger={glowFileTrigger}
            glowLoadManuscript={glowLoadManuscript}
            onFileMenuOpen={onFileMenuOpen}
            onLoadManuscriptClicked={onLoadManuscriptClicked}
          />
          <EditMenu />
          <ViewMenu visible={visible} togglePanel={togglePanel} />
          <ModeMenu />
          <GenreMenu />
          {!simpleMode && (
            <ToolsMenu
              onBuyTokens={onBuyTokens}
              visible={visible} togglePanel={togglePanel}
              onOpenFlowPass={() => setFlowPassOpen(true)}
              user={user}
            />
          )}
          <SetupMenu
            onNewFromTemplate={onNewFromTemplate}
            onImportManuscript={onImportManuscript}
            visible={visible} togglePanel={togglePanel}
            onOpenSynopsisHub={() => setSynopsisHubOpen(true)}
            onOpenModelRoles={() => setModelRolesOpen(true)}
            onOpenAiAudit={() => setAiAuditOpen(true)}
          />
          {!simpleMode && (
            <AdvancedModelMenu visible={visible} togglePanel={togglePanel} />
          )}
          <HelpMenu onHelp={onHelp} onContextHelp={onContextHelp} onCommunity={onCommunity} onAbout={onAbout} />
        </div>
      </div>

      {/* ── Right side: mode toggle, pills, exports, Ask AI, Rebuild ── */}
      <div className="flex items-center gap-1.5 py-1 pr-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant={simpleMode ? 'default' : 'outline'}
              size="sm"
              className="h-7 gap-1.5 px-2.5 text-[11.5px]"
              onClick={onToggleSimpleMode}
              data-testid="menu-simple-mode-toggle"
            >
              {simpleMode ? <Feather className="h-3 w-3" /> : <SlidersHorizontal className="h-3 w-3" />}
              {simpleMode ? t('menu.simpleMode') : t('menu.advancedMode')}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom" className="max-w-[220px] text-[11.5px]">
            {simpleMode ? t('menu.simpleModeTooltipOn') : t('menu.simpleModeTooltipOff')}
          </TooltipContent>
        </Tooltip>
        <WordCreditBalancePill onOpenBuy={() => onBuyTokens?.()} />
        <ManuscriptCostPill />
        <RunningCostPill onClick={() => onOpenSettings?.('budget')} />
        <ExportsIndicator onOpen={onExport} />
        <LanguageCapsule variant="compact" />
        <Button
          variant="ghost" size="sm"
          className={`h-7 px-2 text-[12px] ${claudeChatCap ? '' : 'opacity-55 cursor-not-allowed'}`}
          data-testid="menubar-ask-claude"
          onClick={() => {
            if (!claudeChatCap) {
              const cta = upgradeCta('claude_chat');
              toast.info(cta.title, { description: `${cta.body} · Upgrade in Settings → Subscription.`, duration: 4500 });
              return;
            }
            toast.success(`Suggestions generated by ${handle}`);
          }}
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5 text-primary" /> {t('menu.askHandle', { handle })}
          {!claudeChatCap && <Lock className="ml-1.5 h-3 w-3 text-muted-foreground" />}
        </Button>
        <Button
          size="sm" disabled={building} onClick={() => { setWizardOpen(true); rebuildNovel(); }}
          className="h-7 px-3 text-[12px] bg-primary text-primary-foreground hover:bg-primary/90 shadow-inset-top"
        >
          {building ? <Hammer className="mr-1.5 h-3.5 w-3.5 animate-pulse" /> : <Play className="mr-1.5 h-3.5 w-3.5" />}
          {building ? t('menu.rebuilding') : t('menu.commitAndRebuild')}
        </Button>
      </div>

      {/* ── Dialogs ── */}
      {/* Exit dialog */}
      <Dialog open={exitOpen} onOpenChange={setExitOpen}>
        <DialogContent className="max-w-sm" data-testid="file-exit-dialog">
          <div className="space-y-1 pb-1">
            <p className="font-semibold">{t('menu.exitTitle')}</p>
            <p className="text-sm text-muted-foreground">{t('menu.exitDescription')}</p>
          </div>
          <div className="flex flex-col gap-2 pt-2">
            <Button type="button" variant="outline" className="justify-start gap-2"
              onClick={() => { setExitOpen(false); navigate('/'); }} data-testid="file-exit-landing">
              {t('menu.exitBackToLanding')}
              <span className="ml-auto text-[10.5px] text-muted-foreground">{t('menu.exitKeepSignedIn')}</span>
            </Button>
            <Button type="button" variant="outline" className="justify-start gap-2"
              onClick={() => { setExitOpen(false); navigate('/admin'); }} data-testid="file-exit-admin">
              {t('menu.exitAdminConsole')}
              <span className="ml-auto text-[10.5px] text-muted-foreground">{t('menu.exitRequiresAdminSignIn')}</span>
            </Button>
            <Button type="button" variant="destructive" className="justify-start gap-2"
              onClick={async () => {
                setExitOpen(false);
                try { await logout?.(); } catch (_e) { /* noop */ }
                navigate('/');
              }} data-testid="file-exit-logout">
              {t('menu.exitLogOut')}
              <span className="ml-auto text-[10.5px] opacity-80">{t('menu.exitEndSession')}</span>
            </Button>
          </div>
          <div className="mt-2 flex justify-end">
            <Button type="button" variant="ghost" onClick={() => setExitOpen(false)} data-testid="file-exit-cancel">
              {t('common.cancel')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Premier-only Bulk Novel Wizard */}
      <BulkNovelWizard
        open={bulkOpen}
        onOpenChange={(o) => { setBulkOpen(o); if (!o) setBulkPreloadFwId(''); }}
        initialFrameworkId={bulkPreloadFwId}
      />
      {/* Admin-only Novel Factory — bulk synopsis → full manuscript pipeline */}
      <NovelFactoryWizard
        open={novelFactoryOpen}
        onClose={() => setNovelFactoryOpen(false)}
      />
      {/* Premier-only Studio Production Dashboard */}
      <StudioProductionDashboard />
      {/* Training Center — self-mounting singleton */}
      <TrainingCenter />
      {/* Synopsis Tools Hub */}
      <SynopsisToolsHub
        open={synopsisHubOpen}
        onOpenChange={setSynopsisHubOpen}
        onGenerateSynopsisFromLogline={onGenerateSynopsisFromLogline}
        onImportSynopsis={onImportSynopsis}
        onImportThreeActSynopsis={onImportThreeActSynopsis}
        onBuildSynopsis={onBuildSynopsis}
        togglePanel={togglePanel}
        onMetadataExtract={() => { setSynopsisHubOpen(false); setMetadataExtractOpen(true); }}
      />
      {/* Metadata Extraction Dialog */}
      <MetadataExtractionDialog
        open={metadataExtractOpen}
        onOpenChange={setMetadataExtractOpen}
      />
      {/* Model Roles Dialog */}
      <ModelRolesDialog
        open={modelRolesOpen}
        onOpenChange={setModelRolesOpen}
        projectId={project?.id}
      />
      {/* AI Content Audit Dialog */}
      <AiContentAuditDialog
        open={aiAuditOpen}
        onOpenChange={setAiAuditOpen}
        projectId={project?.id}
        data-testid="ai-content-audit-dialog"
      />
      {/* Flow Pass — "Polish for readability" */}
      <Dialog open={flowPassOpen} onOpenChange={setFlowPassOpen}>
        <DialogContent className="max-w-lg p-0 gap-0 h-[90vh] flex flex-col overflow-hidden" data-testid="flow-pass-dialog">
          <FlowPassPanel
            project={project}
            chapter={chapters?.find(c => c.id === activeChapterId)}
            onAccept={(polished) => {
              window.dispatchEvent(new CustomEvent('loom:set-chapter-content', { detail: { content: polished } }));
            }}
            onClose={() => setFlowPassOpen(false)}
          />
        </DialogContent>
      </Dialog>
      {/* Rebuild Wizard — per-chapter progress overlay */}
      <RebuildWizardDialog
        open={wizardOpen && building}
        chapters={chapters}
        buildJob={buildJob}
        buildProgress={buildProgress}
        onClose={() => setWizardOpen(false)}
      />
    </div>
  );
}
